import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { DataTableColumn, PaginatorStatus, SortConfiguration } from '../../generics/data-table/data-table.component';
import { ErrorResponse } from 'src/app/models/error.model';
import { ServicioContratado } from 'src/app/models/servicio-contratado.model';
import { TipoProducto } from 'src/app/models/cobertura.model';
import { Zona } from 'src/app/models/zona.model';
import { ServiciosContratadosService } from 'src/app/services/servicios-contratados.service';
import { ZonasService } from 'src/app/services/zonas.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
    selector: 'app-servicios-contratados',
    templateUrl: './servicios-contratados.component.html',
    styleUrls: ['./servicios-contratados.component.scss']
})
export class ServiciosContratadosComponent implements OnInit, OnChanges {

    @Input() embedded = false;
    @Input() tipoProductoInicial?: TipoProducto;

    @ViewChild('zonaColumnTemplate', { static: true })
    zonaColumnTemplate: TemplateRef<any>;

    @ViewChild('activoColumnTemplate', { static: true })
    activoColumnTemplate: TemplateRef<any>;

    @ViewChild('actionsColumnTemplate', { static: true })
    actionsColumnTemplate: TemplateRef<any>;

    paginatorStatus: PaginatorStatus = {
        itemsPerPage: 50,
        page: 0,
        totalElementCount: 0
    };

    columns: DataTableColumn[] = [
        { name: 'Nombre', prop: 'nombre', sortable: true },
        { name: 'Código', prop: 'codigo', width: '90px' },
        { name: 'Base', prop: 'base', width: '100px' },
        { name: 'Variable', prop: 'variable', width: '90px', parseValue: (v: number) => `${v}%` },
        { name: 'Mín/mes', prop: 'minPorMes', width: '110px', parseValue: (v: number) => v.toLocaleString('es-AR') },
        { name: 'Máx/mes', prop: 'maxPorMes', width: '110px', parseValue: (v: number) => v.toLocaleString('es-AR') },
        { name: 'Pricing', prop: 'pricingFijo', width: '80px' },
        { name: 'Zona', width: '120px' },
        { name: 'Estado', width: '90px' },
        { name: '', width: '90px' }
    ];

    tipoProductoActual: TipoProducto = 'VIVIENDA';
    selectedZonaId: number | null = null;
    sortConfiguration?: SortConfiguration;
    servicios: ServicioContratado[] = [];
    zonas: Zona[] = [];

    get displayServicios(): (ServicioContratado & { heredado?: boolean })[] {
        if (this.selectedZonaId === null) {
            return this.servicios.filter(s => !s.idZona);
        }
        const defaults = this.servicios.filter(s => !s.idZona && s.activo);
        const zoneItems = this.servicios.filter(s => s.idZona === this.selectedZonaId && s.activo);
        const merged = defaults.map(def => {
            const override = zoneItems.find(z => z.codigo === def.codigo);
            return override ?? { ...def, heredado: true };
        });
        const orphans = zoneItems.filter(zi => !defaults.find(d => d.codigo === zi.codigo));
        return [...merged, ...orphans];
    }

    constructor(
        private serviciosService: ServiciosContratadosService,
        private zonasService: ZonasService,
        private utilsService: UtilsService
    ) { }

    ngOnInit(): void {
        if (this.tipoProductoInicial) {
            this.tipoProductoActual = this.tipoProductoInicial;
        }
        this.columns.find(col => col.name === 'Zona').template = this.zonaColumnTemplate;
        this.columns.find(col => col.name === 'Estado').template = this.activoColumnTemplate;
        this.columns.find(col => col.name === '').template = this.actionsColumnTemplate;
        this.loadZonas();
        this.getServicios();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tipoProductoInicial'] && !changes['tipoProductoInicial'].firstChange) {
            this.tipoProductoActual = this.tipoProductoInicial;
            this.selectedZonaId = null;
            this.paginatorStatus.page = 0;
            this.getServicios();
        }
    }

    loadZonas() {
        this.zonasService.getZonas(0, 200, [], { 'ocultar-inactivos': 'false' }).subscribe({
            next: response => { this.zonas = response.body ?? []; }
        });
    }

    getZonaLabel(idZona?: number): string {
        if (!idZona) return 'Default';
        return this.zonas.find(z => z.id === idZona)?.nombre ?? `Zona ${idZona}`;
    }

    getServicios() {
        this.serviciosService.getServiciosContratados(
            this.paginatorStatus.page,
            this.paginatorStatus.itemsPerPage,
            this.sortConfiguration ? [this.sortConfiguration] : [],
            this.tipoProductoActual,
            false
        ).subscribe({
            next: response => {
                this.paginatorStatus.totalElementCount = parseInt(response.headers.get('pagination-total-count') ?? '0', 10);
                this.servicios = response.body;
            },
            error: (error: ErrorResponse) => this.utilsService.alert('Error al obtener servicios contratados', error.error?.descripcion)
        });
    }

    onTipoChange(tipo: TipoProducto) {
        this.tipoProductoActual = tipo;
        this.paginatorStatus.page = 0;
        this.getServicios();
    }

    onZonaChange(idZona: number | null) {
        this.selectedZonaId = idZona;
    }

    revertirADefault(servicio: ServicioContratado) {
        const dialog = this.utilsService.confirm(
            '¿Revertir este servicio al Default?',
            `"${servicio.nombre}" pasará a la configuración base (sin zona específica).`
        );
        dialog.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.serviciosService.updateServicioContratado({ ...servicio, idZona: null }).subscribe({
                    next: () => {
                        this.utilsService.alert('Servicio revertido', `"${servicio.nombre}" ahora pertenece al Default.`);
                        this.getServicios();
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al revertir', error.error?.descripcion)
                });
            }
        });
    }

    updatePage(event: PageEvent) {
        this.paginatorStatus.itemsPerPage = event.pageSize;
        this.paginatorStatus.page = event.pageIndex;
        this.getServicios();
    }

    updateSort(event: SortConfiguration) {
        this.sortConfiguration = event;
        this.getServicios();
    }

    deleteServicio(servicio: ServicioContratado) {
        const dialog = this.utilsService.confirm(
            '¿Dar de baja este servicio contratado?',
            `"${servicio.nombre}" quedará inactivo.`
        );
        dialog.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.serviciosService.deleteServicioContratado(servicio.id).subscribe({
                    next: () => {
                        this.utilsService.alert('Servicio dado de baja', `"${servicio.nombre}" fue dado de baja correctamente.`);
                        this.getServicios();
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al dar de baja', error.error?.descripcion)
                });
            }
        });
    }
}
